import React, { useCallback, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useSetCalculationPeriod } from '@features/scope/calculation-period.hooks';
import { usePhasesCardStyles } from './phases-card.styles';

type LifetimeEditorProps = {
  lifetime: number;
  active: boolean;
  onHoverChange?: (hovered: boolean) => void;
  placement?: 'top' | 'bottom';
};

/** Inline editable building lifetime shown in the Operation group label. */
export const LifetimeEditor: React.FC<LifetimeEditorProps> = ({
  lifetime,
  active,
  onHoverChange,
  placement = 'top',
}) => {
  const { classes, cx } = usePhasesCardStyles();
  const [editing, setEditing] = useState(false);
  const setCalculationPeriod = useSetCalculationPeriod();
  // Captures the lifetime value at the moment the user clicks, so the ref
  // callback can use it without depending on the prop (which would cause React
  // to replace the ref and re-focus the element if lifetime changes mid-edit).
  const initialLifetimeRef = useRef(lifetime);

  const editingRef = useCallback((el: HTMLSpanElement | null) => {
    if (!el) return;
    el.textContent = String(initialLifetimeRef.current);
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
  }, []);

  const handleNumberClick = useCallback<React.MouseEventHandler>(
    (e) => {
      if (!active) return;
      e.stopPropagation();
      initialLifetimeRef.current = lifetime;
      onHoverChange?.(false);
      setEditing(true);
    },
    [active, lifetime, onHoverChange],
  );

  const commitEdit = useCallback<React.FocusEventHandler<HTMLSpanElement>>(
    (e) => {
      const parsed = parseInt(e.currentTarget.textContent ?? '', 10);
      if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 1000) {
        setCalculationPeriod(parsed);
      }
      setEditing(false);
    },
    [setCalculationPeriod],
  );

  const handleKeyDown = useCallback<
    React.KeyboardEventHandler<HTMLSpanElement>
  >((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Escape') {
      setEditing(false);
      return;
    }
    const isDigit = e.key.length === 1 && /\d/.test(e.key);
    const isAllowedControl =
      e.key.length > 1 || e.ctrlKey || e.metaKey || e.altKey;
    if (!isDigit && !isAllowedControl) e.preventDefault();
  }, []);

  const handleMouseEnter = useCallback(
    () => onHoverChange?.(true),
    [onHoverChange],
  );
  const handleMouseLeave = useCallback(
    () => onHoverChange?.(false),
    [onHoverChange],
  );

  if (editing) {
    return (
      <span
        className={cx(classes.lifetimeNumber, classes.lifetimeEditing)}
        contentEditable
        role="textbox"
        aria-label="Calculation period"
        suppressContentEditableWarning
        ref={editingRef}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        onBeforeInput={(e) => {
          const data = (e.nativeEvent as InputEvent).data;
          if (data && !/^\d+$/.test(data)) e.preventDefault();
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <Tooltip
      title={active ? 'Edit lifetime' : ''}
      placement={placement}
      describeChild
    >
      <span
        className={cx(
          classes.lifetimeNumber,
          !active && classes.lifetimeNumberInactive,
        )}
        onClick={handleNumberClick}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {lifetime}
      </span>
    </Tooltip>
  );
};
